import React, { useCallback } from 'react';
import Autocomplete, {
  AutocompleteRenderInputParams,
  createFilterOptions
} from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import makeStyles from '@mui/styles/makeStyles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import classNames from 'classnames';
import { IconButton } from '@mui/material';
import RemoveIcon from '@mui/icons-material/RemoveCircle';
import { CustomLabel } from 'modules/timesheet-labels/labelSchema';
import { useMessage } from 'modules/intl';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex'
  },
  remove: {
    color: theme.palette.error.main,
    padding: theme.spacing(0),
    margin: theme.spacing(0, 0, 0, 1)
  },
  addLi: {
    display: 'flex',
    padding: '0px !important',
    color: theme.palette.primary.main
  },
  li: {
    display: 'flex'
  }
}));

type CustomLabelKeySelectorProps = {
  className?: string;
  labels: CustomLabel[];
  selectedKeys: Set<string>;
  value?: CustomLabel;
  onChange: (label: CustomLabel | null) => void;
  onRemove: () => void;
};

export const CustomLabelKeySelector: React.FC<CustomLabelKeySelectorProps> = ({
  labels,
  selectedKeys,
  value,
  onChange,
  onRemove
}: CustomLabelKeySelectorProps) => {
  const classes = useStyles();
  const hintLabel = useMessage('searchBox.clKeyHint');

  const getOptionLabel = useCallback(
    (option: CustomLabel) => option.name ?? '',
    []
  );

  const renderOption = useCallback(
    (props: React.HTMLAttributes<HTMLLIElement>, option) => {
      return (
        <li
          {...props}
          className={classNames(classes.li, props.className)}
          data-testid={`li-${option.id}`}
        >
          <Tooltip title={option.name} disableInteractive>
            <Typography variant="inherit" noWrap>
              {option.name}
            </Typography>
          </Tooltip>
        </li>
      );
    },
    [classes.li]
  );

  const filterOptions = useCallback((options: CustomLabel[], state) => {
    const defaultFilterOptions = createFilterOptions<CustomLabel>();
    const filteredOptions = defaultFilterOptions(options, state);

    return filteredOptions;
  }, []);

  const renderInput = useCallback(
    (params: AutocompleteRenderInputParams) => (
      <TextField
        {...params}
        placeholder={hintLabel}
        variant="standard"
        size="small"
        inputProps={{
          ...params.inputProps,
          'data-testid': 'custom-label-key-input'
        }}
      />
    ),
    [hintLabel]
  );
  const getOptionDisabled = useCallback(
    (label: CustomLabel) => selectedKeys.has(label.id),
    [selectedKeys]
  );

  const handleChange = useCallback(
    (_, value: CustomLabel | null) => {
      onChange(value);
    },
    [onChange]
  );

  return (
    <div className={classes.root}>
      <Autocomplete<CustomLabel>
        data-testid="custom-label-key-search"
        key={value?.name}
        value={value}
        options={labels}
        fullWidth
        onChange={handleChange}
        getOptionLabel={getOptionLabel}
        renderOption={renderOption}
        filterOptions={filterOptions}
        renderInput={renderInput}
        getOptionDisabled={getOptionDisabled}
      />
      <IconButton onClick={onRemove} className={classes.remove}>
        <RemoveIcon />
      </IconButton>
    </div>
  );
};
